import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Snackbar, Alert, Box, Typography, Paper, IconButton, Button, Tooltip, Card, CardContent, } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Navbar from "../components/Navbar";
import HeroBanner from "../components/HeroBanner";
import { useAuth } from "../context/AuthContext";
import EditProfileModal from "../components/EditProfileModal"; // imported modal
import ChangeAccountTypeModal from "../components/ChangeAccountTypeModal";
import { modelItems } from "../data/SiteData";

const Profile = () => {
  const { user } = useAuth();
  const { id } = useParams();
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // State for modals
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [isChangeAccountModalOpen, setChangeAccountModalOpen] = useState(false);

  const handleOpenChangeAccountModal = () => setChangeAccountModalOpen(true); // ✅ NEW HANDLER
  const handleCloseChangeAccountModal = () => setChangeAccountModalOpen(false);

  const messages = {
    1: "This demonstration prototype uses mock data and is not connected to a database. Profile changes will not be seen",
    2: "This demonstration prototype uses mock data and is not connected to a database. Account type requests are not processed. Please log in as a different user to see account differences",
    3: "This demonstration prototype uses mock data and is not connected to a database. Sign up applications can not be saved. You have been logged in as the default demonstration account",
    4: "Editing or uploading a model is not available in this demonstration prototype.",
    5: "Curating a model is not available in this demonstration prototype.",
    6: "Approving user requests is not available in this demonstration prototype.",
  };

  useEffect(() => {
    if (id && messages[id]) {
      setSnackbarMessage(messages[id]);
      setOpenSnackbar(true);
    }
  }, [id]);

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const uploadedModel = modelItems.find((m) => m.modelId === "model2");
  const curatedModel = modelItems.find((m) => m.modelId === "model3");

  if (!user) {
    return (
      <div>
        <Navbar />
        <HeroBanner
          heroTitle="Account"
          heroDescription="Please log in to view your profile"
          imageUrl="/images/digiTwins.png"
          height="165px"
        />
        <Box sx={{ textAlign: "center", mt: 4 }}>
          <Typography variant="body1">You are not logged in.</Typography>
        </Box>
      </div>
    );
  }

  const Row = ({ label, value, type }) => {
    const renderValue = () => {
      if (!value) return "-";

      if (type === "xlink") {
        return (
          <a
            href={`https://twitter.com/${value}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#489596",
              textDecoration: "none",
              fontFamily: "PT Sans, sans-serif",
              fontSize: "18px",
              fontWeight: 400,
            }}
            onMouseOver={(e) => (e.currentTarget.style.textDecoration = "underline")}
            onMouseOut={(e) => (e.currentTarget.style.textDecoration = "none")}
          >
            @{value}
          </a>
        );
      }

      if (type === "linkedin") {
        return (
          <a
            href={`https://www.linkedin.com/in/${value}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#489596",
              textDecoration: "none",
              fontFamily: "PT Sans, sans-serif",
              fontSize: "18px",
              fontWeight: 400,
            }}
            onMouseOver={(e) => (e.currentTarget.style.textDecoration = "underline")}
            onMouseOut={(e) => (e.currentTarget.style.textDecoration = "none")}
          >
            {value}
          </a>
        );
      }

      return value;
    };

    return (
      <Box sx={{ display: "flex", mb: 1, alignItems: "center" }}>
        <Box
          sx={{
            width: 180,
            textAlign: "left",
            fontFamily: "PT Sans, sans-serif",
            fontSize: "18px",
            fontWeight: 500,
            color: "#142434",
          }}
        >
          {label}
        </Box>
        <Box
          sx={{
            textAlign: "left",
            fontFamily: "PT Sans, sans-serif",
            fontSize: "18px",
            fontWeight: 400,
            color: "#142434",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {renderValue()}
        </Box>
      </Box>
    );
  };

  const accountTypeMap = {
    "1": "Standard",
    "2": "Model submitter",
    "3": "Curator",
    "4": "Administrator",
  };
  const accountTypeName = accountTypeMap[user.accType] || user.accType;

  return (
    <div>
      <Navbar />
      <HeroBanner
        heroTitle="Account"
        heroDescription="Welcome to your account profile page"
        imageUrl="/images/digiTwins.png"
        height="165px"
      />

      {/* Profile Details Section */}
      <Box
        sx={{
          mx: "64px",
          my: 4,
          border: "0.5px solid #489596",
          p: 3,
        }}
      >
        <Typography
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "24px",
            fontWeight: 400,
            color: "#142434",
            mb: 2,
          }}
        >
          PROFILE DETAILS:
        </Typography>

        <Row label="Name:" value={`${user.firstname} ${user.lastname}`} />
        <Row label="Email:" value={user.email} />
        <Row label="ORCID:" value={user.orcid} />
        <Row label="About me:" value={user.about} />
        <Row label="Institution/Company:" value={user.institution} />
        <Row label="X (Twitter):" value={user.xlink} type="xlink" />
        <Row label="LinkedIn:" value={user.linkedin} type="linkedin" />

        {/* Edit Profile Button */}
        <Box sx={{ mt: 3 }}>
          <Button
            onClick={() => setEditModalOpen(true)} // wired modal
            sx={{
              width: "240px",
              height: "42px",
              backgroundColor: "#FFFFFF",
              border: "2px solid #489596",
              fontFamily: "Poppins, sans-serif",
              fontSize: "16px",
              fontWeight: 600,
              color: "#489596",
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { backgroundColor: "#ecf1f1" },
            }}
          >
            Edit profile details
          </Button>
        </Box>
      </Box>

      {/* Account Details Section */}
      <Box
        sx={{
          mx: "64px",
          my: 4,
          border: "0.5px solid #489596",
          p: 3,
        }}
      >
        <Typography
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "24px",
            fontWeight: 400,
            color: "#142434",
            mb: 2,
          }}
        >
          ACCOUNT DETAILS:
        </Typography>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontFamily: "PT Sans, sans-serif",
              fontSize: "18px",
              fontWeight: 500,
              color: "#142434",
              minWidth: 180,
            }}
          >
            Account Type:
          </Typography>
          <Typography
            sx={{
              fontFamily: "PT Sans, sans-serif",
              fontSize: "18px",
              fontWeight: 400,
              color: "#142434",
            }}
          >
            {accountTypeName}
          </Typography>
          <Tooltip
            title={
              <span style={{ fontFamily: "PT Sans, sans-serif", fontSize: "14px", lineHeight: "1.4" }}>
                Four account types exist:<br />
                <b>Standard</b> - This is the account for general users<br />
                <b>Model submitter</b> - This account allows users to submit and maintain their models on the portal<br />
                <b>Curator</b> - This account is especially for users who curate submitted models to check for validity and completeness before they are available for others on the portal<br />
                <b>Administrator</b> - This account is restricted and allows users to maintain the portal
              </span>
            }
            arrow
          >
            <HelpOutlineIcon sx={{ color: "#489596", cursor: "pointer" }} />
          </Tooltip>
        </Box>

        {/* Change Account Type Button */}
        <Box mt={3}>
          <Button
            variant="outlined"
            onClick={handleOpenChangeAccountModal} // ✅ OPENS NEW MODAL
            sx={{
              width: "240px",
              height: "42px",
              fontFamily: "Poppins, sans-serif",
              fontSize: "16px",
              fontWeight: 600,
              textTransform: "none",
              color: "#489596",
              backgroundColor: "#FFFFFF",
              border: "2px solid #489596",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#E9F5F5",
                boxShadow: "none",
              },
            }}
          >
            Change account type
          </Button>
        </Box>
      </Box>

      {user.accType !== "1" && (
        <Box
          sx={{
            mx: "64px",
            my: 4,
            border: "0.5px solid #489596",
            p: 3,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "24px",
              fontWeight: 400,
              color: "#142434",
              mb: 2,
            }}
          >
            UPLOADED MODELS:
          </Typography>

          {uploadedModel ? (
            <>
              <Card
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  mb: 2,
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  boxShadow: "none",
                  p: 2,
                  width: "100%",
                  maxWidth: "900px", // ✅ widen
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mr: 2 }}>
                  <Box
                    component="img"
                    src={uploadedModel.image}
                    alt={uploadedModel.title}
                    onClick={() => {
                      setSnackbarMessage(messages[4]);
                      setOpenSnackbar(true);
                    }}
                    sx={{
                      width: "150px", // slightly larger for wider card
                      height: "auto",
                      borderRadius: "4px",
                      objectFit: "cover",
                      cursor: "pointer",
                      mb: 1,
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setSnackbarMessage(messages[4]);
                      setOpenSnackbar(true);
                    }}
                    sx={{
                      width: "120px",
                      height: "32px",
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "14px",
                      fontWeight: 600,
                      textTransform: "none",
                      color: "#489596",
                      border: "2px solid #489596",
                      "&:hover": { backgroundColor: "#E9F5F5" },
                    }}
                  >
                    Edit Model
                  </Button>
                </Box>

                <CardContent sx={{ padding: 0 }}>
                  <Typography
                    sx={{
                      fontFamily: "Poppins, sans-serif",
                      fontSize: "18px",
                      fontWeight: 500,
                      color: "#489596",
                      mb: "4px",
                    }}
                  >
                    {uploadedModel.title}
                  </Typography>

                  <Typography sx={{ fontFamily: "PT Sans", fontSize: "14px", color: "#142434" }}>
                    {uploadedModel.date}
                  </Typography>

                  <Typography sx={{ fontFamily: "PT Sans", fontSize: "15px", color: "#142434", mt: 1 }}>
                    <strong>Version:</strong> {uploadedModel.version}
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: "PT Sans",
                      fontSize: "15px",
                      color: "#142434",
                      lineHeight: 1.4,
                      mt: 1,
                    }}
                  >
                    {uploadedModel.description}
                  </Typography>
                </CardContent>
              </Card>

              <Button
                variant="outlined"
                onClick={() => {
                  setSnackbarMessage(messages[4]);
                  setOpenSnackbar(true);
                }}
                sx={{
                  width: "200px",
                  height: "40px",
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "16px",
                  fontWeight: 600,
                  textTransform: "none",
                  color: "#489596",
                  border: "2px solid #489596",
                  "&:hover": { backgroundColor: "#E9F5F5" },
                }}
              >
                Upload Model
              </Button>
            </>
          ) : (
            <Typography sx={{ fontFamily: "PT Sans, sans-serif", fontSize: "18px", color: "#142434" }}>
              No uploaded models available.
            </Typography>
          )}
        </Box>
      )}



      {/* Model Curation (Shown only for Curators & Administrators) */}
      {(user.accType === "3" || user.accType === "4") && (
        <Box
          sx={{
            mx: "64px",
            my: 4,
            border: "0.5px solid #489596",
            p: 3,
          }}
        >
          <Typography
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "24px",
              fontWeight: 400,
              color: "#142434",
              mb: 2,
            }}
          >
            MODEL CURATION:
          </Typography>

          {curatedModel ? (
            <Card
              sx={{
                display: "flex",
                alignItems: "flex-start",
                mb: 2,
                border: "1px solid #ddd",
                borderRadius: "8px",
                boxShadow: "none",
                p: 2,
                width: "100%",
                maxWidth: "900px",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mr: 2 }}>
                <Box
                  component="img"
                  src={curatedModel.image}
                  alt={curatedModel.title}
                  onClick={() => {
                    setSnackbarMessage(messages[5]);
                    setOpenSnackbar(true);
                  }}
                  sx={{
                    width: "150px",
                    height: "auto",
                    borderRadius: "4px",
                    objectFit: "cover",
                    cursor: "pointer",
                    mb: 1,
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={() => {
                    setSnackbarMessage(messages[5]);
                    setOpenSnackbar(true);
                  }}
                  sx={{
                    width: "140px",
                    height: "32px",
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "14px",
                    fontWeight: 600,
                    textTransform: "none",
                    color: "#489596",
                    border: "2px solid #489596",
                    "&:hover": { backgroundColor: "#E9F5F5" },
                  }}
                >
                  Curate Model
                </Button>
              </Box>

              <CardContent sx={{ padding: 0 }}>
                <Typography
                  sx={{
                    fontFamily: "Poppins, sans-serif",
                    fontSize: "18px",
                    fontWeight: 500,
                    color: "#489596",
                    mb: "4px",
                  }}
                >
                  {curatedModel.title}
                </Typography>

                <Typography sx={{ fontFamily: "PT Sans", fontSize: "14px", color: "#142434", mb: 1 }}>
                  {curatedModel.date}
                </Typography>

                {/* No version line here on purpose */}

                <Typography
                  sx={{
                    fontFamily: "PT Sans",
                    fontSize: "15px",
                    color: "#142434",
                    lineHeight: 1.4,
                    mt: 1,
                  }}
                >
                  {curatedModel.description}
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Typography sx={{ fontFamily: "PT Sans, sans-serif", fontSize: "18px", color: "#142434" }}>
              No models in curation queue.
            </Typography>
          )}
        </Box>
      )}


      {user.accType === "4" && (
  <Box
    sx={{
      mx: "64px",
      my: 4,
      border: "0.5px solid #489596",
      p: 3,
    }}
  >
    <Typography
      sx={{
        fontFamily: "Poppins, sans-serif",
        fontSize: "24px",
        fontWeight: 400,
        color: "#142434",
        mb: 2,
      }}
    >
      PERMISSION APPROVALS:
    </Typography>

    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap", // ensures buttons don't overflow on small screens
      }}
    >
      <Typography sx={{ fontFamily: "PT Sans, sans-serif", fontSize: "18px", color: "#142434" }}>
        {"Sam Cooper (sam@example.com) has requested an account upgrade. Standard -> Curator"}
      </Typography>

      <Button
        variant="outlined"
        onClick={() => {
          setSnackbarMessage(messages[6]);
          setOpenSnackbar(true);
        }}
        sx={{
          width: "100px",
          height: "32px",
          fontFamily: "Poppins, sans-serif",
          fontSize: "14px",
          fontWeight: 600,
          textTransform: "none",
          color: "#489596",
          border: "2px solid #489596",
          "&:hover": { backgroundColor: "#E9F5F5" },
        }}
      >
        Approve
      </Button>

      <Button
        variant="outlined"
        onClick={() => {
          setSnackbarMessage(messages[6]);
          setOpenSnackbar(true);
        }}
        sx={{
          width: "100px",
          height: "32px",
          fontFamily: "Poppins, sans-serif",
          fontSize: "14px",
          fontWeight: 600,
          textTransform: "none",
          color: "#489596",
          border: "2px solid #489596",
          "&:hover": { backgroundColor: "#E9F5F5" },
        }}
      >
        Reject
      </Button>
    </Box>
  </Box>
)}




      {/* Info Snackbar */}
      <Snackbar
        open={openSnackbar}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ top: "16px", width: "auto", padding: "0 16px", zIndex: 1300 }}
      >
        <Alert
          severity="info"
          onClose={handleCloseSnackbar}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleCloseSnackbar}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{
            width: "100%",
            border: "1px solid",
            borderColor: "info.main",
            p: 2,
            fontFamily: "PT Sans, sans-serif",
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Edit Profile Modal */}
      <EditProfileModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
      />
      <ChangeAccountTypeModal
        open={isChangeAccountModalOpen}
        onClose={handleCloseChangeAccountModal}
      />
    </div>
  );
};

export default Profile;
